import React, { useState, useRef, useEffect } from 'react';

const ImageCrop = ({ src, onCropComplete, onCancel, aspectRatio = 16 / 9 }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeHandle, setResizeHandle] = useState('');
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [startCrop, setStartCrop] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [maintainRatio, setMaintainRatio] = useState(true);
    const imageRef = useRef(null);

    useEffect(() => {
        if (imageRef.current) {
            const img = imageRef.current;
            const { width, height } = img.getBoundingClientRect();
            setImageSize({ width, height });

            // Set initial crop to 80% of image, centered
            const cropWidth = width * 0.8;
            const cropHeight = maintainRatio ? cropWidth / aspectRatio : height * 0.6;

            setCrop({
                x: (width - cropWidth) / 2,
                y: (height - cropHeight) / 2,
                width: cropWidth,
                height: cropHeight
            });
        }
    }, [src, aspectRatio, maintainRatio]);

    const handleMouseDown = (e, action, handle = '') => {
        e.preventDefault();
        e.stopPropagation();

        setStartPos({ x: e.clientX, y: e.clientY });
        setStartCrop({ ...crop });

        if (action === 'drag') {
            setIsDragging(true);
        } else if (action === 'resize') {
            setIsResizing(true);
            setResizeHandle(handle);
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging && !isResizing) return;

        const deltaX = e.clientX - startPos.x;
        const deltaY = e.clientY - startPos.y;

        if (isDragging) {
            setCrop(prev => {
                const newX = Math.max(0, Math.min(imageSize.width - prev.width, startCrop.x + deltaX));
                const newY = Math.max(0, Math.min(imageSize.height - prev.height, startCrop.y + deltaY));
                return { ...prev, x: newX, y: newY };
            });
        } else if (isResizing) {
            setCrop(prev => {
                let newCrop = { ...startCrop };

                switch (resizeHandle) {
                    case 'se': // Bottom right
                        newCrop.width = Math.max(50, Math.min(imageSize.width - startCrop.x, startCrop.width + deltaX));
                        if (maintainRatio) {
                            newCrop.height = newCrop.width / aspectRatio;
                        } else {
                            newCrop.height = Math.max(50, Math.min(imageSize.height - startCrop.y, startCrop.height + deltaY));
                        }
                        break;
                    case 'sw': // Bottom left
                        const newWidthSW = Math.max(50, startCrop.width - deltaX);
                        newCrop.x = Math.max(0, startCrop.x + startCrop.width - newWidthSW);
                        newCrop.width = newWidthSW;
                        if (maintainRatio) {
                            newCrop.height = newCrop.width / aspectRatio;
                        } else {
                            newCrop.height = Math.max(50, Math.min(imageSize.height - startCrop.y, startCrop.height + deltaY));
                        }
                        break;
                    case 'ne': // Top right
                        newCrop.width = Math.max(50, Math.min(imageSize.width - startCrop.x, startCrop.width + deltaX));
                        if (maintainRatio) {
                            const newHeightNE = newCrop.width / aspectRatio;
                            newCrop.y = Math.max(0, startCrop.y + startCrop.height - newHeightNE);
                            newCrop.height = newHeightNE;
                        } else {
                            const newHeightNE = Math.max(50, startCrop.height - deltaY);
                            newCrop.y = Math.max(0, startCrop.y + startCrop.height - newHeightNE);
                            newCrop.height = newHeightNE;
                        }
                        break;
                    case 'nw': // Top left
                        const newWidthNW = Math.max(50, startCrop.width - deltaX);
                        newCrop.x = Math.max(0, startCrop.x + startCrop.width - newWidthNW);
                        newCrop.width = newWidthNW;
                        if (maintainRatio) {
                            const newHeightNW = newCrop.width / aspectRatio;
                            newCrop.y = Math.max(0, startCrop.y + startCrop.height - newHeightNW);
                            newCrop.height = newHeightNW;
                        } else {
                            const newHeightNW = Math.max(50, startCrop.height - deltaY);
                            newCrop.y = Math.max(0, startCrop.y + startCrop.height - newHeightNW);
                            newCrop.height = newHeightNW;
                        }
                        break;
                    case 'n': // Top edge
                        const newHeightN = Math.max(50, startCrop.height - deltaY);
                        newCrop.y = Math.max(0, startCrop.y + startCrop.height - newHeightN);
                        newCrop.height = newHeightN;
                        if (maintainRatio) {
                            newCrop.width = newCrop.height * aspectRatio;
                        }
                        break;
                    case 's': // Bottom edge
                        newCrop.height = Math.max(50, Math.min(imageSize.height - startCrop.y, startCrop.height + deltaY));
                        if (maintainRatio) {
                            newCrop.width = newCrop.height * aspectRatio;
                        }
                        break;
                    case 'e': // Right edge
                        newCrop.width = Math.max(50, Math.min(imageSize.width - startCrop.x, startCrop.width + deltaX));
                        if (maintainRatio) {
                            newCrop.height = newCrop.width / aspectRatio;
                        }
                        break;
                    case 'w': // Left edge
                        const newWidthW = Math.max(50, startCrop.width - deltaX);
                        newCrop.x = Math.max(0, startCrop.x + startCrop.width - newWidthW);
                        newCrop.width = newWidthW;
                        if (maintainRatio) {
                            newCrop.height = newCrop.width / aspectRatio;
                        }
                        break;
                }

                // Ensure crop doesn't exceed image boundaries
                if (newCrop.x + newCrop.width > imageSize.width) {
                    newCrop.width = imageSize.width - newCrop.x;
                }
                if (newCrop.y + newCrop.height > imageSize.height) {
                    newCrop.height = imageSize.height - newCrop.y;
                }

                return newCrop;
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setIsResizing(false);
        setResizeHandle('');
    };

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, startPos, startCrop, imageSize, maintainRatio, aspectRatio]);

    const getCroppedImage = () => {
        if (!imageRef.current) return;

        const img = imageRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate scale factors
        const scaleX = img.naturalWidth / imageSize.width;
        const scaleY = img.naturalHeight / imageSize.height;

        // Crop at 2x the intended display size for better quality
        const cropScale = 2;
        canvas.width = crop.width * scaleX * cropScale;
        canvas.height = crop.height * scaleY * cropScale;

        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw the cropped portion at higher resolution
        ctx.drawImage(
            img,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height
        );

        // Use PNG for lossless compression if quality is critical
        canvas.toBlob(onCropComplete, 'image/png');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-6xl max-h-full overflow-auto">
                <h3 className="text-lg font-semibold mb-4">Crop Image</h3>

                {/* Aspect ratio toggle */}
                <div className="mb-4 flex items-center space-x-4">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={maintainRatio}
                            onChange={(e) => setMaintainRatio(e.target.checked)}
                            className="mr-2"
                        />
                        Maintain aspect ratio ({aspectRatio.toFixed(2)}:1)
                    </label>
                </div>

                <div className="relative inline-block mb-4 max-w-full max-h-[70vh] overflow-auto">
                    <img
                        ref={imageRef}
                        src={src}
                        alt="Crop preview"
                        className="max-w-full max-h-full object-contain"
                        style={{ maxWidth: '800px', maxHeight: '600px' }}
                        draggable={false}
                    />

                    {/* Crop overlay */}
                    <div
                        className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 cursor-move"
                        style={{
                            left: crop.x,
                            top: crop.y,
                            width: crop.width,
                            height: crop.height,
                        }}
                        onMouseDown={(e) => handleMouseDown(e, 'drag')}
                    >
                        {/* Corner handles */}
                        <div className="absolute w-3 h-3 bg-blue-500 border border-white cursor-nw-resize" style={{ left: -6, top: -6 }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'nw')} />
                        <div className="absolute w-3 h-3 bg-blue-500 border border-white cursor-ne-resize" style={{ right: -6, top: -6 }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'ne')} />
                        <div className="absolute w-3 h-3 bg-blue-500 border border-white cursor-sw-resize" style={{ left: -6, bottom: -6 }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'sw')} />
                        <div className="absolute w-3 h-3 bg-blue-500 border border-white cursor-se-resize" style={{ right: -6, bottom: -6 }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'se')} />

                        {/* Edge handles - only show when not maintaining ratio */}
                        {!maintainRatio && (
                            <>
                                <div className="absolute w-3 h-3 bg-blue-500 border border-white cursor-n-resize" style={{ left: '50%', top: -6, transform: 'translateX(-50%)' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'n')} />
                                <div className="absolute w-3 h-3 bg-blue-500 border border-white cursor-s-resize" style={{ left: '50%', bottom: -6, transform: 'translateX(-50%)' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 's')} />
                                <div className="absolute w-3 h-3 bg-blue-500 border border-white cursor-e-resize" style={{ right: -6, top: '50%', transform: 'translateY(-50%)' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'e')} />
                                <div className="absolute w-3 h-3 bg-blue-500 border border-white cursor-w-resize" style={{ left: -6, top: '50%', transform: 'translateY(-50%)' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'w')} />
                            </>
                        )}
                    </div>
                </div>

                <div className="mb-4 text-sm text-stone-600 space-y-2">
                    <p>Crop size: {Math.round(crop.width)} × {Math.round(crop.height)} pixels</p>
                    <p>Current ratio: {(crop.width / crop.height).toFixed(2)}:1</p>
                </div>

                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-stone-600 border border-stone-300 rounded hover:bg-stone-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={getCroppedImage}
                        className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600"
                    >
                        Apply Crop
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCrop;